import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { INCIDENT_SEVERITY_LABELS, RELEASE_PRODUCT_LABELS, fromLocalInput, incidentFormProblem, newKey, productErrorMessage, toLocalInput } from "../lib/product";
import { INCIDENT_PEOPLE } from "../components/product/IncidentDialogs";
import { OwnerSelect, usePeople } from "../components/product/ProductDialogs";
import { SelectField } from "../components/tickets/TicketDialogs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

// Nouvel incident (D-40): a page, started now by default; `?reopens=` links it to a closed incident that came back.
export default function ProductIncidentFormPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reopens = params.get("reopens");
  const people = usePeople(true, INCIDENT_PEOPLE);
  const [previous, setPrevious] = useState(null);
  const [draft, setDraft] = useState({ title: "", product: "", severity: "", startedAt: toLocalInput(new Date().toISOString()), detectedAt: "", impact: "", workaround: "", responsible: "" });
  const [key, setKey] = useState(newKey);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!reopens) return;
    api.get(`/product/incidents/${reopens}`).then(({ data }) => {
      setPrevious({ id: data.id, number: data.number, title: data.title });
      setDraft((d) => ({ ...d, product: d.product || data.product }));
    }).catch(() => toast.error("Incident introuvable : le nouvel incident ne lui sera pas lié."));
  }, [reopens]);

  // A new content is a new request: the Idempotency-Key only protects against the SAME tap sent twice.
  const change = (patch) => { setDraft((d) => ({ ...d, ...patch })); setKey(newKey()); setError(""); };
  const set = (name) => (value) => change({ [name]: value?.target ? value.target.value : value });
  const problem = useMemo(() => incidentFormProblem(draft) || (!draft.product ? "Choisissez le logiciel concerné." : !draft.severity ? "Choisissez la gravité." : ""), [draft]);
  const critical = draft.severity === "critical" || draft.severity === "major";

  const submit = async () => {
    if (problem || saving) return;
    setSaving(true);
    setError("");
    try {
      const body = {
        title: draft.title.trim(), product: draft.product, severity: draft.severity, started_at: fromLocalInput(draft.startedAt),
        ...(draft.detectedAt ? { detected_at: fromLocalInput(draft.detectedAt) } : {}), impact: draft.impact.trim(), workaround: draft.workaround.trim(),
        ...(draft.responsible ? { responsible_id: draft.responsible } : {}), ...(previous ? { reopens_incident_id: previous.id } : {}),
      };
      const { data } = await api.post("/product/incidents", body, { headers: { "Idempotency-Key": key } });
      toast.success(`Incident ${data.number} ouvert`);
      navigate(`/produit/incidents/${data.id}`);
    } catch (e) {
      setError(productErrorMessage(e, "Impossible d'ouvrir l'incident. Réessayez."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="incident-form-page">
      <Link to="/produit/incidents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Incidents de production</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouvel incident</h1>
        <p className="text-sm text-slate-500">Ouvrez l'incident dès qu'il est constaté ; la chronologie se complète ensuite.</p>
      </div>

      <form className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="space-y-4 min-w-0">
          {previous && (
            <Card className="p-4 text-sm text-slate-700 bg-slate-50" data-testid="incident-reopens">
              Suite de l'incident clos <span className="font-medium tabular-nums">{previous.number}</span> · {previous.title}
            </Card>
          )}
          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-semibold text-slate-900">L'incident</h2>
            <div className="space-y-1.5">
              <Label htmlFor="incident-title">Titre *</Label>
              <Input id="incident-title" value={draft.title} onChange={set("title")} maxLength={140} placeholder="La caisse ne s'ouvre plus après la mise à jour" data-testid="incident-title" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField id="incident-product" label="Logiciel *" value={draft.product} onChange={set("product")} options={RELEASE_PRODUCT_LABELS} />
              <SelectField id="incident-severity" label="Gravité *" value={draft.severity} onChange={set("severity")} options={INCIDENT_SEVERITY_LABELS} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="incident-impact">Impact constaté</Label>
              <Textarea id="incident-impact" value={draft.impact} onChange={set("impact")} rows={4} maxLength={2000} data-testid="incident-impact"
                placeholder="Ce que les pharmacies ne peuvent plus faire, depuis quand" />
              <p className="flex items-start gap-1.5 text-xs text-slate-500"><ShieldAlert size={13} className="shrink-0 mt-0.5" />Aucune donnée patient ni nom de pharmacie : les tickets liés comptent les pharmacies.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="incident-workaround">Contournement</Label>
              <Textarea id="incident-workaround" value={draft.workaround} onChange={set("workaround")} rows={3} maxLength={2000} data-testid="incident-workaround"
                placeholder="Ce qu'on peut faire en attendant (obligatoire pour passer à « contourné »)" />
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-semibold text-slate-900">Quand et qui</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="incident-started">Début *</Label>
                <Input id="incident-started" type="datetime-local" value={draft.startedAt} onChange={set("startedAt")} data-testid="incident-started" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="incident-detected">Détecté</Label>
                <Input id="incident-detected" type="datetime-local" value={draft.detectedAt} onChange={set("detectedAt")} data-testid="incident-detected" />
                <p className="text-xs text-slate-500">Vide : maintenant.</p>
              </div>
            </div>
            <OwnerSelect id="incident-responsible" label="Responsable" value={draft.responsible} onChange={set("responsible")} people={people} />
          </Card>

          {error && <p className="text-sm text-red-600" role="alert" data-testid="incident-form-error">{error}</p>}
          {problem && <p className="text-xs text-slate-500" data-testid="incident-form-problem">{problem}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
            <Button type="submit" disabled={!!problem || saving} data-testid="incident-form-submit">
              {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ouvrir l'incident
            </Button>
          </div>
        </div>

        <aside className="hidden lg:block space-y-3">
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h2 className="font-semibold text-slate-900">Et ensuite ?</h2>
            <p>Ouvert → contourné → résolu → clos. Chaque étape s'ajoute à la chronologie ; les tickets de pharmacies et les actions préventives se rattachent depuis la fiche.</p>
          </Card>
          <Card className={`p-4 space-y-2 text-sm ${critical ? "text-amber-900 bg-amber-50 border-amber-200" : "text-slate-600"}`} data-testid="incident-form-severity-hint">
            <h2 className="font-semibold">Gravité</h2>
            {critical
              ? <p>Un incident {draft.severity === "critical" ? "critique" : "majeur"} prévient les gestionnaires d'incidents par WhatsApp et demande un post-mortem complet avant d'être clos.</p>
              : <p>Mineur : une gêne limitée. Majeur : une fonction importante est touchée. Critique : des pharmacies sont bloquées.</p>}
          </Card>
        </aside>
      </form>
    </div>
  );
}
